import type { Rng } from '../rng';
import type { IncidentKind } from '../mission/incident';

/**
 * Die Spielfigur.
 *
 * Das Aussehen ist die Kennung eines fertigen Portraets, nicht mehr eine
 * Sammlung von Reglerwerten. Der Grund ist schlicht: Sechs Regler auf
 * gezeichneten Ebenen sahen nach Platzhalter aus, und das Erste, was man vom
 * Spiel sieht, sollte nicht nach Platzhalter aussehen. Die Kennung bleibt
 * reine Zeichenkette — `core/` weiss nichts von Bilddateien.
 *
 * Der Hintergrund ist dagegen kein Beiwerk: Jeder der drei wirkt auf ein
 * anderes bestehendes System, und jeder bringt einen Verlust mit, der im
 * Finale wieder auftaucht.
 */

export type Background = 'pilot' | 'naval_officer' | 'marine_biologist';

export const BACKGROUNDS: readonly Background[] = ['pilot', 'naval_officer', 'marine_biologist'];

/**
 * Die waehlbaren Portraets.
 *
 * Die Reihenfolge ist die des Auswahlrasters und Teil des Speicherformats —
 * neue Portraets gehoeren ans Ende, damit bestehende Staende gueltig bleiben.
 * Jede Kennung muss eine Datei unter `public/assets/portraits/` haben;
 * tests/assets.test.ts haelt das fest.
 */
export const PORTRAIT_IDS: readonly string[] = [
  'p01', 'p02', 'p03', 'p04', 'p05',
  'p06', 'p07', 'p08', 'p09', 'p10',
  'p11', 'p12', 'p13', 'p14', 'p15',
];

export type Gender = 'female' | 'male';

/**
 * Wer auf den Portraets zu sehen ist.
 *
 * Von Hand nach den Fotos eingetragen (p01–p06 Frauen, p07–p15 Maenner), damit
 * der vorgeschlagene Name zum Gesicht passt. Kommt ein Portraet hinzu, gehoert
 * es auch hier hinein; tests/character.test.ts haelt das fest.
 */
export const PORTRAIT_GENDER: Readonly<Record<string, Gender>> = {
  p01: 'female', p02: 'female', p03: 'female', p04: 'female', p05: 'female', p06: 'female',
  p07: 'male', p08: 'male', p09: 'male', p10: 'male', p11: 'male', p12: 'male',
  p13: 'male', p14: 'male', p15: 'male',
};

export function genderOfPortrait(portraitId: string): Gender {
  return PORTRAIT_GENDER[portraitId] ?? 'male';
}

/** Das Portraet der Institutsleitung. Bewusst nicht waehlbar. */
export const ADVISER_PORTRAIT_ID = 'roskopf';

export interface Character {
  readonly name: string;
  /** Kennung aus {@link PORTRAIT_IDS}. */
  readonly portraitId: string;
  readonly background: Background;
  /** Name der Person, die verloren ging. */
  readonly lostName: string;
  /** Uebersetzungsschluessel des Verwandtschafts- oder Dienstverhaeltnisses. */
  readonly lostRelationKey: string;
}

/**
 * Was ein Hintergrund kann.
 *
 * Bewusst je genau eine Wirkung, und jede auf ein System, das es schon gibt.
 * Drei kleine, klar unterscheidbare Vorteile sind lesbarer als neun
 * verrechnete Prozentwerte.
 */
export interface BackgroundEffects {
  /**
   * Faktor auf die Unsicherheit der Prognose. Kleiner heisst sicherer.
   * Der Pilot liest Absturzmuster, die andere raten muessen.
   */
  forecastUncertaintyFactor(kind: IncidentKind): number;
  /** Faktor auf die Rumpfbelastung. Der Marineoffizier faehrt die See besser ab. */
  readonly hullStressFactor: number;
  /**
   * Zusaetzliche Hinweise bei kalten Spuren. Die Meeresbiologin liest die
   * Umgebung, wo andere nur leeres Wasser sehen.
   */
  readonly coldTrailBonusClues: number;
}

const AIRBORNE_KINDS: readonly IncidentKind[] = ['airliner', 'light_aircraft'];

const EFFECTS: Readonly<Record<Background, BackgroundEffects>> = {
  pilot: {
    // Bei Luftfahrzeugen weiss der Pilot, was ein Aufschlag anrichtet und wie
    // lange eine Rettungsinsel haelt — die Schaetzung wird deutlich schaerfer.
    forecastUncertaintyFactor: (kind) => (AIRBORNE_KINDS.includes(kind) ? 0.35 : 1),
    hullStressFactor: 1,
    coldTrailBonusClues: 0,
  },
  naval_officer: {
    forecastUncertaintyFactor: () => 1,
    // Wer jahrelang schwere See gefahren ist, schont den Rumpf.
    hullStressFactor: 0.6,
    coldTrailBonusClues: 0,
  },
  marine_biologist: {
    forecastUncertaintyFactor: () => 1,
    hullStressFactor: 1,
    // Wo nichts mehr treibt, bleibt immer noch das Wasser selbst.
    coldTrailBonusClues: 1,
  },
};

export function backgroundEffects(background: Background): BackgroundEffects {
  return EFFECTS[background];
}

/**
 * Wen die Figur verloren hat — je Hintergrund ein anderes Verhaeltnis.
 * Der Verlust ist der Grund, warum sie hier draussen ist.
 */
export const LOST_RELATION_KEY: Readonly<Record<Background, string>> = {
  pilot: 'background.lost.wingman',
  naval_officer: 'background.lost.crew',
  marine_biologist: 'background.lost.parents',
};

/**
 * Das Geschlecht der verlorenen Person — es folgt dem Wort fuer die Beziehung.
 *
 * „Ihr Rottenflieger“ und „Ihr Bootsmann“ sind maennlich, „Ihre Mutter“ ist
 * weiblich. Vorher wurde der Name frei gewuerfelt, und es stand zum Beispiel
 * „Verloren: Miriam Lindqvist — Ihr Rottenflieger“ da.
 */
export const LOST_GENDER: Readonly<Record<Background, Gender>> = {
  pilot: 'male',
  naval_officer: 'male',
  marine_biologist: 'female',
};

export const GIVEN_NAMES: Readonly<Record<Gender, readonly string[]>> = {
  female: ['Ellis', 'Salome', 'Nadia', 'Thea', 'Ruth', 'Vera', 'Miriam', 'Lene', 'Hanna', 'Ilse'],
  male: ['Marek', 'Idris', 'Junot', 'Cormac', 'Ivo', 'Anselm', 'Kai', 'Osian', 'Jonas', 'Emil'],
};

const SURNAMES = [
  'Vandermeer', 'Okonkwo', 'Ferreira', 'Lindqvist', 'Amari', 'Brennan',
  'Kowalczyk', 'Halloran', 'Duarte', 'Nakamura', 'Sorensen', 'Achterberg',
] as const;

/** Ein Name; ohne Angabe des Geschlechts wird auch das gewuerfelt. */
export function randomName(rng: Rng, gender?: Gender): string {
  const chosen = gender ?? (rng.bool() ? 'female' : 'male');
  return `${rng.pick(GIVEN_NAMES[chosen])} ${rng.pick(SURNAMES)}`;
}

export function randomPortraitId(rng: Rng): string {
  return rng.pick(PORTRAIT_IDS);
}

export function createCharacter(rng: Rng, overrides: Partial<Character> = {}): Character {
  const background = overrides.background ?? rng.pick(BACKGROUNDS);
  // Erst das Gesicht, dann der Name dazu.
  const portraitId = overrides.portraitId ?? randomPortraitId(rng);
  return {
    name: overrides.name ?? randomName(rng, genderOfPortrait(portraitId)),
    portraitId,
    background,
    lostName: overrides.lostName ?? randomName(rng.derive('lost'), LOST_GENDER[background]),
    lostRelationKey: LOST_RELATION_KEY[background],
  };
}

/** Blaettert im Auswahlraster weiter, mit Umlauf. */
export function cyclePortrait(portraitId: string, delta: number): string {
  const at = PORTRAIT_IDS.indexOf(portraitId);
  const count = PORTRAIT_IDS.length;
  const next = (((at < 0 ? 0 : at) + delta) % count + count) % count;
  return PORTRAIT_IDS[next] ?? PORTRAIT_IDS[0]!;
}
